import { useState, type CSSProperties } from "react";
import { cn } from "../lib/utils";

interface JsonViewerProps {
  data: unknown;
  initialDepth?: number;
  className?: string;
  style?: CSSProperties;
  name?: string;
}

function JsonNode({ value, name, depth, initialDepth }: { value: unknown; name?: string; depth: number; initialDepth: number }) {
  const [open, setOpen] = useState(depth < initialDepth);

  if (value === null) return <Line name={name}><span className="jv-null">null</span></Line>;
  if (value === undefined) return <Line name={name}><span className="jv-null">undefined</span></Line>;
  if (typeof value === "boolean") return <Line name={name}><span className="jv-bool">{String(value)}</span></Line>;
  if (typeof value === "number") return <Line name={name}><span className="jv-num">{value}</span></Line>;
  if (typeof value === "string") return <Line name={name}><span className="jv-str">&quot;{value}&quot;</span></Line>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <Line name={name}><span className="jv-bracket">[]</span></Line>;
    return (
      <div className="jv-node">
        <button className="jv-toggle" onClick={() => setOpen(o => !o)} aria-expanded={open}>
          <span className="jv-arrow">{open ? "▾" : "▸"}</span>
          {name && <><span className="jv-key">&quot;{name}&quot;</span><span className="jv-colon">: </span></>}
          <span className="jv-bracket">[</span>
          {!open && <span className="jv-collapsed">{value.length} items</span>}
          {!open && <span className="jv-bracket">]</span>}
        </button>
        {open && (
          <div className="jv-children">
            {value.map((v, i) => <JsonNode key={i} value={v} name={String(i)} depth={depth + 1} initialDepth={initialDepth} />)}
            <span className="jv-bracket">]</span>
          </div>
        )}
      </div>
    );
  }

  if (typeof value === "object") {
    const keys = Object.keys(value as object);
    if (keys.length === 0) return <Line name={name}><span className="jv-bracket">{"{}"}</span></Line>;
    return (
      <div className="jv-node">
        <button className="jv-toggle" onClick={() => setOpen(o => !o)} aria-expanded={open}>
          <span className="jv-arrow">{open ? "▾" : "▸"}</span>
          {name && <><span className="jv-key">&quot;{name}&quot;</span><span className="jv-colon">: </span></>}
          <span className="jv-bracket">{"{"}</span>
          {!open && <span className="jv-collapsed">{keys.length} keys</span>}
          {!open && <span className="jv-bracket">{"}"}</span>}
        </button>
        {open && (
          <div className="jv-children">
            {keys.map(k => <JsonNode key={k} value={(value as Record<string, unknown>)[k]} name={k} depth={depth + 1} initialDepth={initialDepth} />)}
            <span className="jv-bracket">{"}"}</span>
          </div>
        )}
      </div>
    );
  }

  return <Line name={name}><span className="jv-str">{String(value)}</span></Line>;
}

function Line({ name, children }: { name?: string; children: React.ReactNode }) {
  return (
    <div className="jv-line">
      {name !== undefined && <><span className="jv-key">&quot;{name}&quot;</span><span className="jv-colon">: </span></>}
      {children}
    </div>
  );
}

export function JsonViewer({ data, initialDepth = 1, name = "root", className, style }: JsonViewerProps) {
  return (
    <div className={cn("jv-root", className)} style={style}>
      <JsonNode value={data} name={name} depth={0} initialDepth={initialDepth} />
    </div>
  );
}
